import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "react-query";
import { SessionExpirationModal } from "../AuthProvider";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import { ErrorBoundary } from "react-error-boundary";
import { AxiosError } from "axios";
import { usePostHog } from "posthog-js/react";
import { CORE_ANALYTICS_EVENTS } from "@/lib/posthog-constants";
interface BSErrorBoundaryProps {
  children: React.ReactNode;
  title: string;
  FallbackComponent?: React.ComponentType<{
    error: any;
  }>;
  fallback?: React.ReactNode | ((props: {
    error: any;
    resetErrorBoundary: () => void;
  }) => React.ReactNode);
}

/**
 * Default fallback component that shows a simple error message
 */
const DefaultFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-[300px] border border-dashed rounded-lg flex items-center justify-center text-neutral-300 dark:text-neutral-600 text-sm">Something went wrong, please try refreshing the page</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * A custom error boundary that handles common error scenarios like session expiration
 * and error reporting to Sentry and PostHog.
 */
export function BSErrorBoundary(t0) {
  const $ = _c(11);
  const {
    children,
    title,
    FallbackComponent: t1,
    fallback
  } = t0;
  const FallbackComponent = t1 === undefined ? DefaultFallback : t1;
  const queryClient = useQueryClient();
  const postHog = usePostHog();
  let t2;
  if ($[0] !== FallbackComponent || $[1] !== fallback || $[2] !== postHog || $[3] !== queryClient || $[4] !== title) {
    t2 = (error, resetErrorBoundary) => {
      const data = queryClient.getQueryData("connectedGithubOrgs");
      const activeGithubOrg = (data as {
        active_org_name: string | null;
      })?.active_org_name ?? null;
      Sentry.withScope(scope => {
        if (activeGithubOrg) {
          scope.setExtra("activeGithubOrg", activeGithubOrg);
        }
        scope.setExtra("errorBoundaryTitle", title);
        const errorWithTitle = new Error(`[${title}] ${error.message}`);
        errorWithTitle.stack = error.stack;
        Sentry.captureException(errorWithTitle);
      });
      console.error(`[${title}] error boundary triggered with error`, error);
      postHog?.capture(CORE_ANALYTICS_EVENTS.ERROR, {
        error,
        errorBoundaryTitle: title,
        orgName: activeGithubOrg,
        stackTrace: error?.stack || undefined
      });
      if (error instanceof AxiosError && error.response?.status === 401) {
        return <SessionExpirationModal open={true} setOpen={_temp} />;
      }
      if (fallback !== undefined) {
        if (typeof fallback === "function") {
          return fallback({
            error,
            resetErrorBoundary
          });
        }
        return fallback;
      }
      return <FallbackComponent error={error} />;
    };
    $[0] = FallbackComponent;
    $[1] = fallback;
    $[2] = postHog;
    $[3] = queryClient;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleError = t2;
  let t3;
  if ($[6] !== handleError) {
    t3 = t4 => {
      const {
        error: error_0,
        resetErrorBoundary: resetErrorBoundary_0
      } = t4;
      return handleError(error_0, resetErrorBoundary_0);
    };
    $[6] = handleError;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== t3) {
    t4 = <ErrorBoundary fallbackRender={t3}>{children}</ErrorBoundary>;
    $[8] = children;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function _temp() {}